import { getAssignmentDataSourceByKey } from '@gonfalon/openapi/operations/getAssignmentDataSourceByKey';
import { getAssignmentDataSources } from '@gonfalon/openapi/operations/getAssignmentDataSources';
import { patchAssignmentDataSource } from '@gonfalon/openapi/operations/patchAssignmentDataSource';
import { postAssignmentDataSource } from '@gonfalon/openapi/operations/postAssignmentDataSource';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

export const getAssignmentDataSourcesQuery = createQueryOptions(getAssignmentDataSources);

export const getAssignmentDataSourceByKeyQuery = createQueryOptions(getAssignmentDataSourceByKey);

export {
  assignmentSourceProbeQuery,
  postAssignmentDataSourceProbeQuery,
  setAssignmentSourceProbeQueryData,
  setAssignmentSourceProbeQueryDataFromExperiment,
  toAssignmentSourceProbeIsoDateTime,
} from './assignmentSourceProbe';
export type { AssignmentSourceProbeQueryParams } from './assignmentSourceProbe';

function hasAssignmentDataSourceRevision<T>(revision: T): revision is NonNullable<T> {
  return (
    revision !== null && revision !== undefined && !(typeof revision === 'object' && Object.keys(revision).length === 0)
  );
}

type AssignmentDataSourceGetExpansions = {
  experimentKeys?: string[];
  experimentsCount?: number;
  _integrationConfigurationSummary?: {
    name: string;
    id: string;
  };
};

function mergeAssignmentDataSourceGetExpansions<T extends AssignmentDataSourceGetExpansions>(
  revision: T,
  previous: T | undefined,
): T {
  if (!previous) {
    return revision;
  }

  return {
    ...revision,
    experimentKeys: revision.experimentKeys ?? previous.experimentKeys,
    experimentsCount: revision.experimentsCount ?? previous.experimentsCount,
    _integrationConfigurationSummary:
      revision._integrationConfigurationSummary ?? previous._integrationConfigurationSummary,
  };
}

export const useCreateAssignmentDataSource = createMutationHook(postAssignmentDataSource, {
  onSuccess: async (_data, input) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: getAssignmentDataSourcesQuery.partialQueryKey({
        projKey: input.projKey,
      }),
    });
  },
});

export const useUpdateAssignmentDataSource = createMutationHook(patchAssignmentDataSource, {
  onSuccess: async (data, input) => {
    const queryClient = getQueryClient();

    if (hasAssignmentDataSourceRevision(data)) {
      queryClient.setQueryData(
        getAssignmentDataSourceByKeyQuery({
          projKey: input.projKey,
          dataSourceKey: input.dataSourceKey,
        }).queryKey,
        (previous: typeof data | undefined) => mergeAssignmentDataSourceGetExpansions(data, previous),
      );
    }

    await queryClient.invalidateQueries({
      queryKey: getAssignmentDataSourcesQuery.partialQueryKey({
        projKey: input.projKey,
      }),
    });
  },
});
